import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { authActions } from "../../store/authReducer";

import { Box, Card, CardContent, CardActions, Grid, Typography, Button } from "@mui/material";
import LoginIcon from "@mui/icons-material/Login";
import { useMsal } from "@azure/msal-react";

import { isTokenValid } from "../../utils/helpers";
import { uiActions } from "../../store/uiReducer";
import api from "../../utils/api";
import LoadingFallback from "../LoadingFallback";

export default function Login() {
  const { instance, accounts, inProgress } = useMsal();

  const hasLoggedOut = sessionStorage.getItem("hub-app-logged-out");
  const isProcessingLogin = sessionStorage.getItem("hub-app-processing-login");

  const [isProcessingToken, setIsProcessingToken] = useState(!hasLoggedOut || isProcessingLogin);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const session = useSelector((state) => state.auth);
  const hasNavigated = useRef(false);

  // Navigate to appropriate destination after login
  const navigateAfterLogin = useCallback(() => {
    // Prevent double navigation
    if (hasNavigated.current) {
      return;
    }

    const initURL = sessionStorage.getItem("initURL");

    hasNavigated.current = true;
    
    if (initURL) {
      sessionStorage.removeItem("initURL");
      navigate(initURL, { replace: true });
    } else {
      navigate(`${process.env.PUBLIC_URL}/dashboard`, { replace: true });
    }
  }, [navigate]);

  const handleLogin = async () => {
    setIsProcessingToken(true);

    sessionStorage.setItem("hub-app-processing-login", "true"); // Persist across redirect
    sessionStorage.removeItem("hub-app-logged-out"); // Clear logout flag

    try {
      await instance.loginRedirect({
        scopes: ["User.Read"],
      });
    } catch (err) {
      console.error("Login error:", err);
      sessionStorage.removeItem("hub-app-processing-login");

      setIsProcessingToken(false);
      dispatch(uiActions.setErrors(err));
    }
  };

  // Verify and get logged user information from backend
  const loginCallback = useCallback(
    async (msToken) => {
      try {
        const response = await api.post("/auth/login", { accessToken: msToken });

        if (!response?.data?.token || !response?.data?.tokenData) {
          throw new Error("Missing token data from callback");
        }

        const { email, name, scp, exp } = response.data.tokenData;
        const permissions = Array.isArray(scp) ? scp : [scp];
        const newSession = { token: response.data.token, email, name, permissions, exp };

        localStorage.setItem("hub-app-session", JSON.stringify(newSession));
        dispatch(authActions.setSession(newSession));

        api.defaults.headers.common["Authorization"] = `Bearer ${response.data.token}`;

        navigateAfterLogin();
      } catch (err) {
        console.log("Login callback error:", err);
        dispatch(uiActions.setErrors(err));
      } finally {
        setIsProcessingToken(false);
      }
    },
    [dispatch, navigateAfterLogin],
  );

  // If already authenticated, try to get token silently
  const getMsToken = useCallback(async () => {
    let token = "";

    // 1. First check for redirect response
    const redirectResponse = await instance.handleRedirectPromise();
    token = redirectResponse?.accessToken;

    if (redirectResponse?.accessToken) {
      token = redirectResponse.accessToken;
      // 2. If no redirect, try silent token acquisition for existing accounts
    } else if (accounts.length > 0) {
      await instance
        .acquireTokenSilent({
          scopes: ["User.Read"],
          account: accounts[0],
        })
        .then((response) => {
          if (response?.accessToken) {
            token = response.accessToken;
          }
        })
        .catch((err) => {
          console.log("Silent token acquisition failed:", err);
        });
    }

    sessionStorage.removeItem("hub-app-processing-login"); // Clear the flag before callback

    if (token) {
      loginCallback(token);
    } else {
      console.log("No token acquired");

      setIsProcessingToken(false);
    }
  }, [accounts, instance, loginCallback]);

  // Main effect: handle session check and MS token acquisition
  useEffect(() => {
    // If already have a valid session, redirect immediately
    if (isTokenValid(session)) {
      navigateAfterLogin();
      return;
    }

    // Otherwise, proceed with MS authentication if needed
    if (inProgress === "none" && isProcessingToken) {
      (async () => {
        await getMsToken();
      })();
    }
  }, [getMsToken, inProgress, isProcessingToken, navigateAfterLogin, session]);

  //Loading also shows after redirect
  if (inProgress !== "none" || isProcessingToken || isProcessingLogin) {
    return <LoadingFallback message="Logging in..." />;
  }

  return (
    <Grid container alignItems="center" justifyContent="center" height="100vh" width="100%">
      <Grid size={{ xs: 12, md: 6 }}>
        <Card raised>
          <Box style={{ backgroundColor: "black" }} mx="auto" p={2} display="flex" justifyContent="center">
            <img src={`${process.env.PUBLIC_URL}/images/Litco-Law-PO-170.png`} alt="Litco Law Logo" />
          </Box>

          <Grid container direction="column" alignItems="center" mt={2} width="100%">
            <CardContent>
              <Typography variant="body1" gutterBottom>
                Welcome to the Litco Law HUB
              </Typography>
            </CardContent>

            <CardActions>
              <Button
                disabled={inProgress !== "none" || isProcessingToken}
                startIcon={<LoginIcon />}
                onClick={handleLogin}
                variant="contained"
                size="small"
                sx={{ width: "100px" }}
              >
                Login
              </Button>
            </CardActions>
          </Grid>
        </Card>
      </Grid>
    </Grid>
  );
}
